import { ContentType } from '../enums/ContentType'
import { PostStatus } from '../enums/PostStatus.js'
import { GoogleBusinessConnectionStatus, SocialMedia } from '../enums/SocialMedia.js'

/**
 * Resolve the active social media account a company would publish to for a given
 * platform. Returns null when the company has no active connection for it.
 *
 * Shared between the History details view (SavedContentsTab) and the publishing
 * success modal so both classify a target the same way.
 */
export function getSelectedAccount(company, platform) {
  if (!company?.connectedSocialMediaTypes) return null

  if (platform === SocialMedia.FACEBOOK) {
    return company.connectedSocialMediaTypes.facebook?.find((page) => page.isActive) || null
  }

  if (platform === SocialMedia.INSTAGRAM) {
    return (
      company.connectedSocialMediaTypes.facebook?.find(
        (page) =>
          page.instagram_business_account && page.instagram_business_account.isActive !== false
      )?.instagram_business_account || null
    )
  }

  if (platform === SocialMedia.LINKEDIN) {
    return company.connectedSocialMediaTypes.linkedin?.find((page) => page.isActive) || null
  }

  if (platform === SocialMedia.GOOGLE_BUSINESS) {
    const googleBusiness = company.connectedSocialMediaTypes.googleBusiness
    // GBP is connected for this company only once a location has been selected.
    if (googleBusiness?.status !== GoogleBusinessConnectionStatus.CONNECTED) return null
    return { name: googleBusiness.locationName || null }
  }

  return null
}

/**
 * Classify a single publishing target (one office × one platform) into a
 * PostStatus. `account` is the resolved active account (see getSelectedAccount);
 * when it is null the target counts as NOT_CONNECTED.
 *
 * `hasActiveConnection` is the office-level flag from getMasterBatchPosts: when
 * the backend marks an office row as not connected, every one of its targets is
 * NOT_CONNECTED regardless of the (placeholder) post payload.
 */
export function getPostStatus(post, account, contentType, hasActiveConnection = true) {
  if (hasActiveConnection === false || post?.hasActiveConnection === false)
    return PostStatus.NOT_CONNECTED
  if (post.permalink) return PostStatus.SUCCESSFUL
  if (post.hasFailed || post.failureReason) return PostStatus.FAILED
  if (!account) return PostStatus.NOT_CONNECTED
  if (contentType === ContentType.SCHEDULED_CONTENT || post.scheduledAt) return PostStatus.SCHEDULED
  return PostStatus.FAILED
}

/**
 * Aggregate the account-level results of a master-view publishing batch.
 *
 * `batchRows` is the `getMasterBatchPosts` result (one row per office, each with
 * a `socialMediaPosts` array covering every selected platform — including the
 * synthetic placeholder rows the backend returns for not-connected offices).
 * `companies` is the `getCompaniesPostingInfo` list used to resolve accounts.
 *
 * Counting each office × platform as a single publishing target keeps the modal
 * totals identical to the History details, since both consume the same source
 * and the same status logic.
 */
export function summarizeBatchPosts(batchRows, companies = [], contentType) {
  const summary = {
    offices: 0,
    accounts: 0,
    successful: 0,
    failed: 0,
    notConnected: 0,
    scheduled: 0,
  }

  const rows = Array.isArray(batchRows) ? batchRows : []
  const companyIds = new Set()

  rows.forEach((row) => {
    if (row?.companyId) companyIds.add(row.companyId)
    const company = companies.find((c) => c.id === row?.companyId) || null
    const hasActiveConnection = row?.hasActiveConnection
    const posts = row?.socialMediaPosts || []

    posts.forEach((post) => {
      summary.accounts += 1
      const platform = post.type?.toUpperCase()
      const account = getSelectedAccount(company, platform)
      const status = getPostStatus(post, account, contentType, hasActiveConnection)

      if (status === PostStatus.SUCCESSFUL) summary.successful += 1
      else if (status === PostStatus.FAILED) summary.failed += 1
      else if (status === PostStatus.NOT_CONNECTED) summary.notConnected += 1
      else if (status === PostStatus.SCHEDULED) summary.scheduled += 1
    })
  })

  summary.offices = companyIds.size
  return summary
}
